// LiveOrders - the counter screen.
// Every order as it lands, itemized with what the customer paid per line;
// drinks / desserts / chips lines highlighted so the counter sees what must
// go in the bag. Menu list prices live on their own page (MenuPrices).
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';
import socket from '../config/socket';
import '../alerts/orderAlarm.css';

const api = axios.create({ baseURL: API_URL, withCredentials: true });

const EMOJI = { drinks: '🥤', desserts: '🍰', chips: '▲', takeout: '🥢' };
// Emoji only come in a red triangle; chips get a text triangle painted yellow.
const Icon = ({ c }) => (c === 'chips'
  ? <span style={{ color: '#facc15' }} aria-label="chips">▲</span>
  : <span aria-label={c}>{EMOJI[c]}</span>);
const LABEL = { drinks: 'drinks', desserts: 'dessert', chips: 'chips', takeout: 'take-out items' };
const SOURCE_COLOR = {
  Toast: 'bg-amber-500 text-black', GrubHub: 'bg-orange-500 text-black', ChowNow: 'bg-teal-500 text-black',
  UberEats: 'bg-green-500 text-black', DoorDash: 'bg-red-500 text-white', Caviar: 'bg-purple-500 text-white',
  InStore: 'bg-gray-500 text-white', Kiosk: 'bg-gray-500 text-white', TEST: 'bg-pink-500 text-white',
};
const money = (v) => (v == null ? '' : `$${Number(v).toFixed(2)}`);
// "2026-09-20" + "12:34" -> "09/20 12:34"
const prettyPhone = (d) => (d && d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : d || '');
const shortStamp = (d, t) => `${d ? `${d.slice(5, 7)}/${d.slice(8, 10)}` : ''}${t ? ` ${t.slice(0, 5)}` : ''}`.trim();

function flaggedCategoryFor(order, name) {
  const n = String(name || '').toLowerCase().trim();
  for (const c of Object.keys(order.flagged || {})) {
    if (order.flagged[c].some(f => String(f.name).toLowerCase().trim() === n)) return c;
  }
  return null;
}

// Text the customer from the card: find (or create) their profile, ask for the
// message, send it through Txtr's own endpoint. Same flow as the Txtr search box.
async function textCustomer(order) {
  const digits = order.phone;
  try {
    const found = await api.get(`/customer-by-phone?phone=${digits}`);
    let customer = found.data.customer;
    if (!customer) {
      const name = window.prompt('No profile for this number yet. Customer name:', order.customer || '');
      if (!name) return;
      const created = await api.post('/create-customer', {
        phone: prettyPhone(digits), name, since: new Date().getFullYear().toString(),
        last_order: new Date().toISOString().split('T')[0], orders: 0, bb_points: 0, barrio_bucks: 0,
      });
      customer = created.data.customer;
    }
    const body = window.prompt(`Text to ${customer.name || prettyPhone(digits)} (${prettyPhone(digits)}):`);
    if (!body || !body.trim()) return;
    await api.post('/send-message', { to: customer.phone || `+1${digits}`, body: body.trim() });
    window.alert(`Sent to ${customer.name || prettyPhone(digits)}. The thread is in Txtr.`);
  } catch (e) {
    window.alert(`Could not send: ${e.response?.data?.error || e.message}`);
  }
}

function OrderCard({ order, isNew }) {
  const [showPhone, setShowPhone] = useState(false);
  const [openLine, setOpenLine] = useState(null); // index of the line whose Toast description is showing
  const cats = order.categories || [];
  const alert = order.alert;
  const border = cats.length
    ? (alert && alert.heardAt ? 'border-yellow-400' : 'border-red-500')
    : 'border-gray-700';
  // Notification-style badge: total quantity of alarm items on this order.
  const alarmCount = cats.reduce((n, c) => n + order.flagged[c].reduce((m, f) => m + (Number(f.quantity) || 1), 0), 0);
  const alarmBreakdown = cats.map(c => `${order.flagged[c].reduce((m, f) => m + (Number(f.quantity) || 1), 0)} ${LABEL[c]}`).join(' · ');
  // Order-level notes repeat per-item instructions as "Item: text". Show only
  // what is not already boxed under a line (check-level or curbside notes).
  const lineNotes = (order.lines || []).map(l => (l.note || '').trim()).filter(Boolean);
  const footerNotes = String(order.notes || '')
    .split(/;\s+/)
    .map(n => n.trim())
    .filter(n => n && !lineNotes.some(ln => n.endsWith(ln)))
    .join('; ');
  return (
    <div className={`relative bg-gray-800 rounded-xl border-2 ${border} p-4 mb-4 ${isNew ? 'ring-4 ring-yellow-300' : ''} ${order.readyAt ? 'bb-card-ready' : ''}`}>
      {order.readyAt ? (
        <div className="bb-ready-stamp" aria-label="Kitchen marked this order ready">
          READY
          <small>{order.readyMinutes != null ? `in ${order.readyMinutes} min` : new Date(order.readyAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</small>
        </div>
      ) : order.kitchenStatus === 'COOKING' ? (
        <div className="bb-cooking-stamp" aria-label="Kitchen is cooking this order">COOKING</div>
      ) : null}
      {alarmCount > 0 && (
        <span
          className="absolute -top-3 -left-3 min-w-[32px] h-8 px-2 rounded-full bg-red-600 text-white text-base font-black flex items-center justify-center shadow-lg ring-2 ring-gray-900"
          title={alarmBreakdown}
          aria-label={`${alarmCount} alarm items: ${alarmBreakdown}`}
        >
          🚨{alarmCount}
        </span>
      )}
      <div className={`flex items-center gap-2 pb-2 mb-2 border-b-2 border-gray-600 ${alarmCount > 0 ? 'pl-4' : ''}`}>
        <span className={`px-2 py-0.5 rounded font-bold text-sm ${SOURCE_COLOR[order.source] || 'bg-blue-500 text-white'}`}>{order.source}</span>
        <span className="text-gray-300 font-semibold whitespace-nowrap">{order.orderTypeLabel || order.orderType}</span>
        {order.customer && <span className="text-white text-lg font-bold truncate min-w-0 flex-1" title={order.customer}>{order.customer}</span>}
        {order.isNewCustomer && <span className="text-xs bg-green-600 text-white px-1.5 py-0.5 rounded">NEW</span>}
        {(order.phone || order.relayPhone) && (
          <span className="relative flex items-center gap-1">
            {order.phone && (
              <button
                type="button"
                onClick={() => textCustomer(order)}
                className="w-8 h-8 rounded-full bg-blue-600 hover:bg-blue-500 text-white flex items-center justify-center"
                title={`Text ${order.customer || 'this customer'} via Txtr`}
                aria-label="Text customer"
              >💬</button>
            )}
            <button
              type="button"
              onClick={() => setShowPhone(v => !v)}
              className="w-8 h-8 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black flex items-center justify-center"
              title={order.phone ? 'Show phone number' : `Show ${order.source} relay / support number`}
              aria-label="Show phone number"
            >📞</button>
            {showPhone && (
              <span className="absolute top-9 left-0 z-20 bg-white text-gray-900 rounded-lg shadow-xl px-3 py-2 whitespace-nowrap">
                {order.phone && (
                  <div className="text-lg font-bold">
                    <a href={`tel:+1${order.phone}`} className="hover:underline">{prettyPhone(order.phone)}</a>
                    <span className="ml-2 text-xs font-semibold text-gray-500">customer</span>
                  </div>
                )}
                {order.relayPhone && order.relayPhone !== order.phone && (
                  <div className="text-lg font-bold">
                    <a href={`tel:+1${order.relayPhone}`} className="hover:underline">{prettyPhone(order.relayPhone)}</a>
                    <span className="ml-2 text-xs font-semibold text-orange-600">{order.source} relay / support</span>
                  </div>
                )}
                <button type="button" onClick={() => setShowPhone(false)} className="mt-1 text-xs font-semibold text-blue-700">close</button>
              </span>
            )}
          </span>
        )}
        <span className="text-gray-400 text-sm whitespace-nowrap">{shortStamp(order.orderDate, order.orderTime)}</span>
        <span className="ml-auto text-2xl font-black text-green-300 whitespace-nowrap">{money(order.total)}</span>
      </div>


      <div className="divide-y divide-gray-700/60">
        {(order.lines || []).map((line, i) => {
          const cat = flaggedCategoryFor(order, line.name);
          // Only priced add-ons on the ticket count as extras. The gap to the
          // in-store menu price is often platform pricing (GrubHub, DoorDash),
          // not toppings, so it is not shown.
          const extras = (line.mods || []).reduce((sum, m) => sum + (Number(m.price) || 0) * (Number(m.quantity) || 1), 0);
          return (
            <div key={i} className="py-2">
              <div className="flex items-start justify-between gap-3">
                <div
                  className={`text-lg leading-tight ${cat ? 'font-extrabold text-yellow-300' : 'text-white font-semibold'} ${line.description ? 'cursor-pointer select-none' : ''}`}
                  onClick={line.description ? () => setOpenLine(openLine === i ? null : i) : undefined}
                  title={line.description ? (openLine === i ? 'Hide the Toast description' : 'Tap to read the Toast description') : undefined}
                >
                  {cat ? <><Icon c={cat} /> </> : null}{line.quantity}× {line.name}
                  {line.description && <span className="ml-2 text-xs text-blue-300 align-middle">{openLine === i ? '▾' : 'ⓘ'}</span>}
                </div>
                <div className="text-right whitespace-nowrap">
                  <div className="text-3xl font-black text-green-300 font-mono leading-none">{money(line.paid)}</div>
                  {line.quantity > 1 && <div className="text-xs text-gray-400">{money(line.unit)} each</div>}
                  {extras >= 0.01 && (
                    <div className="text-xs text-orange-300">includes {money(extras)} in add-ons</div>
                  )}
                </div>
              </div>
              {openLine === i && line.description && (
                <div className="mt-1 mb-1 ml-6 bg-gray-900/70 border border-blue-900 rounded-lg px-3 py-2 text-sm text-blue-100">
                  {line.menuGroup && <div className="text-xs uppercase tracking-wide text-blue-400 mb-1">{line.menuGroup}</div>}
                  {line.description}
                </div>
              )}
              {line.note && (
                <div className="ml-6 my-1 bg-white text-red-700 font-extrabold text-base leading-normal rounded px-3 py-1.5 shadow">
                  ✎ {line.note}
                </div>
              )}
              {(line.mods || []).map((m, j) => {
                const mcat = flaggedCategoryFor(order, m.name);
                return (
                  <div key={j} className={`flex justify-between gap-3 text-sm ${mcat ? 'font-extrabold text-yellow-300' : 'text-gray-300'}`} style={{ paddingLeft: `${1.5 + (m.depth || 0) * 1.25}rem` }}>
                    <span>{mcat ? <><Icon c={mcat} /> </> : '+ '}{m.quantity > 1 ? `${m.quantity}× ` : ''}{m.name}</span>
                    <span className="font-mono text-orange-300 whitespace-nowrap">{m.price > 0 ? money(m.price) : ''}</span>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {order.flagged && order.flagged.takeout && (
        <div className="mt-2 bg-white text-red-700 font-extrabold text-lg leading-normal rounded-lg px-3 py-2 shadow">
          🥢 TO-GO PACKAGING: YES <span className="text-base">— {order.flagged.takeout.map(f => f.name.replace(/^To-go items:\s*/i, '')).join(', ')}</span>
        </div>
      )}

      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-400">
        {order.subtotal != null && <span>Subtotal {money(order.subtotal)}</span>}
        {order.tax != null && <span>Tax {money(order.tax)}</span>}
        {order.tip != null && order.tip > 0 && <span>Tip {money(order.tip)}</span>}
        {order.paymentType && <span>{order.paymentType}</span>}
        <span className="ml-auto text-gray-500">#{order.id}</span>
      </div>
      {footerNotes && (
        <div className="mt-2 bg-white text-red-700 font-extrabold text-lg leading-normal rounded-lg px-3 py-2 shadow">
          📝 {footerNotes}
        </div>
      )}
      {alert && (
        <div className={`mt-2 text-xs font-semibold ${alert.heardAt ? 'text-green-400' : 'text-red-400'}`}>
          {alert.heardAt
            ? `🔊 alarm heard on ${alert.heardBy || 'a screen'} at ${new Date(alert.heardAt).toLocaleTimeString()}`
            : alert.seenAt
              ? `👀 alarm shown on ${alert.seenBy || 'a screen'} (no sound)`
              : '⚠️ alarm not heard on any screen'}
          {alert.ownerNotifiedAt ? ' · owner texted' : ''}
        </div>
      )}
    </div>
  );
}

export default function LiveOrders({ embedded = false }) {
  const [orders, setOrders] = useState([]);
  const [newIds, setNewIds] = useState(new Set());
  const [error, setError] = useState(null);
  const [onlyFlagged, setOnlyFlagged] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = () => api.get('/alerts/orders', { params: { limit: 40 } })
      .then(r => { if (alive) { setOrders(r.data.orders || []); setError(null); } })
      .catch(e => alive && setError(e.response?.data?.error || e.message));
    load();
    const iv = setInterval(load, 60000);

    const onNew = (order) => {
      setOrders(prev => [order, ...prev.filter(o => o.id !== order.id)].slice(0, 60));
      setNewIds(prev => new Set([...prev, order.id]));
      setTimeout(() => setNewIds(prev => { const n = new Set(prev); n.delete(order.id); return n; }), 60000);
    };
    const onAlert = (a) => {
      if (!a.order || !a.order.id) return;
      setOrders(prev => prev.map(o => o.id === a.order.id ? { ...o, alert: { alertId: a.alertId, createdAt: a.createdAt } } : o));
    };
    const onStatus = (s) => {
      setOrders(prev => prev.map(o => (o.alert && o.alert.alertId === s.alertId)
        ? { ...o, alert: { ...o.alert, heardAt: s.heardAt, heardBy: s.heardBy, seenAt: s.seenAt, seenBy: s.seenBy } }
        : o));
    };
    const onReady = (r) => setOrders(prev => prev.map(o => o.id === r.orderId ? { ...o, readyAt: r.readyAt, readyMinutes: r.readyMinutes, kitchenStatus: 'READY' } : o));
    const onKitchen = (r) => setOrders(prev => prev.map(o => o.id === r.orderId ? { ...o, kitchenStatus: r.kitchenStatus } : o));
    socket.on('newOrder', onNew);
    socket.on('orderAlert', onAlert);
    socket.on('alertStatus', onStatus);
    socket.on('orderReady', onReady);
    socket.on('orderStatus', onKitchen);
    return () => {
      alive = false;
      clearInterval(iv);
      socket.off('newOrder', onNew);
      socket.off('orderAlert', onAlert);
      socket.off('alertStatus', onStatus);
      socket.off('orderReady', onReady);
      socket.off('orderStatus', onKitchen);
    };
  }, []);

  const shown = onlyFlagged ? orders.filter(o => (o.categories || []).length > 0) : orders;

  return (
    <div className={embedded ? 'px-3 pb-8' : 'container mx-auto px-4 pb-8 max-w-4xl'}>
      <div>
          <div className="flex items-center gap-3 mb-3">
            <h2 className="text-xl font-bold">Live Orders</h2>
            <label className="text-sm text-gray-300 flex items-center gap-1 cursor-pointer">
              <input type="checkbox" checked={onlyFlagged} onChange={e => setOnlyFlagged(e.target.checked)} />
              only orders with 🥤🍰<span style={{ color: '#facc15' }}>▲</span>
            </label>
            <span className="ml-auto text-xs text-gray-500">newest first · updates live</span>
          </div>
          {error && <div className="bg-red-900/50 text-red-200 px-3 py-2 rounded mb-3">{error}</div>}
          {shown.length === 0 && !error && <div className="text-gray-400">No orders yet.</div>}
          {shown.map(o => <OrderCard key={o.id} order={o} isNew={newIds.has(o.id)} />)}
      </div>
    </div>
  );
}
